import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, Row, Col, Form, Modal, Spinner, Alert } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CardInput from "../../../Components/CardInput";
import { STRIPR_WARNING_MSG } from "../../../utils";
import { diplayCreditCard } from "../Cards/Utils";

export const required = (value) => !value || value.length === 0 ? "Mandatory field" : undefined;
export const emailValidator = (value) => (value && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ? "Invalid email": undefined);
export const requiredIf = (callBack)=>(value, formValues) => {
    if(callBack(formValues)){
        return required(value);
    }
    return undefined;
}

export const paymentFieldsReqCallback = (key)=>(data)=>{
    const addSubscriptionType = data?.addSubscriptionType;
    if(["transactionId", "transactionMode"].includes(key) && (addSubscriptionType === "MANUAL")){
        return true;
    }
    if((addSubscriptionType === "EXISTING_CARD") && (key === "cardId")){
        return true;
    }
    return false;
}

export const paymentTypeReqCallback = (data)=>{
    const selectedSubscriptionType = data?.selectedSubscriptionType;
    return (((selectedSubscriptionType?.value *(100-selectedSubscriptionType?.discountPercentage))/100) > 0);
}

const addSubscriptionValidators = [
    {
        key: "subscriptionTierId",
        validators: [required]

    },
    {
        key: "subscriptionTypeId",
        validators: [required]
    },
    {
        key: "addSubscriptionType",
        validators: [requiredIf(paymentTypeReqCallback)],
    },
    {
        key: "cardId",
        validators: [requiredIf(paymentFieldsReqCallback("cardId"))]
    },
    {
        key: "transactionId",
        validators: [requiredIf(paymentFieldsReqCallback("transactionId"))]
    },
    {
        key: "transactionMode",
        validators: [requiredIf(paymentFieldsReqCallback("transactionMode"))]
    },
    {
        key: "startDate",
        validators: [required]
    }
];

export const TRANSACTION_MODES = [
    { value: "STRIPE", label: "Stripe" },
    { value: "PAYPAL", label: "PayPal" },
    { value: "BANK_DEPOSIT", label: "Bank Deposit" },
    { value: "DISCOUNT", label: "Discount" },
    { value: "POINTS", label: "Points" },
];

export const ADD_SUBSCRIPTION_TYPES = [
    { value: "EXISTING_CARD", label: "Existing Card" },
    { value: "NEW_CARD", label: "New Card" },
    { value: "MANUAL", label: "Manual" },
];

export const EDIT_SUBSCRIPTION_TYPES = [
    { value: "EXISTING_CARD", label: "Existing Card" },
    { value: "NEW_CARD", label: "New Card" },
];

const AddSubscription = ({ show, customer, cards, handleClose, 
    subscriptionTypes, subscriptionTiers, handleSuccess, stripeKey }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [formData, setFormData] = useState({ values: { startDate: new Date() }, errors: {} });
    const [submitting, setSubmitting] = useState(false);
    const [stripeError, setStripeError] = useState(undefined);
    const [apiError, setApiError] = useState(undefined);

    const selectedSubscriptionType = subscriptionTypes?.find(st => st.id === formData?.values?.subscriptionTypeId);
    let transactionValue;
    if(selectedSubscriptionType){
         transactionValue = ((selectedSubscriptionType?.value *(100-selectedSubscriptionType?.discountPercentage))/100);
    }
    
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(()=>{
        handleChange("selectedSubscriptionType",selectedSubscriptionType)
    },[formData?.values?.subscriptionTypeId])

    const validateForm = (data)=>{
        const errors = {};

        addSubscriptionValidators.forEach(({ key, validators }) => {
            const value = data[key];
            let error;
            validators.forEach(validator => {
                if (!error) {
                    console.log(key,data)
                    error = validator(value,data);
                }
            });
            if (error) {
                errors[key] = error;
            }
        });
        return errors;
    }

    const handleChange = (field, value) => {
        if(field === "subscriptionTierId"){
            handleChange("subscriptionTypeId", '');
        }
        if(field === "subscriptionTypeId"){
            handleChange("addSubscriptionType", '');
        }
        setFormData((prevFormData) => {
            const { values: prevValues, errors: prevErrors } = prevFormData;
            const validators = addSubscriptionValidators.find(({key})=>(key === field))?.validators || [];
            let error;
            validators.forEach(validator => {
                if (!error) {
                    error = validator(value);
                }
            });
            return {
                ...prevFormData,
                values: { ...prevValues, [field]: value },
                errors: { ...prevErrors, [field]: error}
            }
        });
    }

    const onSubmit = async () => {
        const { values: data } = formData;

        const errors = validateForm(data);
        if (Object.keys(errors).length > 0) {
            setFormData((prevFormData) => {
                const { errors: prevErrors } = prevFormData;
                return {
                    ...prevFormData,
                    errors: { ...prevErrors, ...errors }
                }
            })
            return;
        }
        const {addSubscriptionType} = data;
        let newCardToken;
        setSubmitting(true);
        setApiError(undefined);
        setStripeError(undefined);
        if(addSubscriptionType === "NEW_CARD"){
            if (!stripe || !elements) {
                // Stripe.js has not yet loaded.
                // Make sure to disable form submission until Stripe.js has loaded.
                setStripeError("Stripe hasn't loaded yet.");
                setSubmitting(false);
                return;
            }
            const { token:data, error }  = await stripe.createToken(elements.getElement(CardElement));
            if (error) {
                setStripeError(error.message);
                setSubmitting(false);
                return;
            }
            newCardToken = data.id;
        }
        const sanitizedData = {
            userId: customer.profile.id,
            subscriptionTypeId: data?.subscriptionTypeId,
            paymentType: data?.addSubscriptionType || undefined,
            cardId: data?.cardId,
            newCardToken,
            transactionId: data?.transactionId,
            transactionMode: data?.transactionMode,
            transactionValue: transactionValue,
            transactionCurrencyCode: selectedSubscriptionType?.currencyCode,
            startDate: moment(data?.startDate).format("YYYY-MM-DD"),
            notes: data?.notes,
            assignLoyaltyPoints: !!data?.assignLoyaltyPoints
        };

        try {
            await handleSuccess(sanitizedData);
            setSubmitting(false);
        } catch (e) {
            setApiError(e?.message || "System error");
            setSubmitting(false);
            console.log(e)
        }

    }
    const addSubscriptionType = formData?.values?.addSubscriptionType;
    const noExistingCards = !(cards?.length !== 0);
    const subscriptionTierId = formData?.values?.subscriptionTierId
    const availableSubscriptionTypes = (subscriptionTypes || []).filter(st=>(!subscriptionTierId || (st?.subscriptionTier?.id === subscriptionTierId)));
    const availableSubscriptionTiers = (subscriptionTiers || []).filter(st=>(st?.code !== "LEGACY"));
    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Subscription for {customer.profile.firstName + ' ' + customer.profile.lastName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form >
                    <Form.Group as={Row} className="mb-3" controlId="frmSubscriptionTier">
                        <Form.Label column sm={3}>Subscription Tier*</Form.Label>
                        <Col sm={9}>
                            <Form.Select
                                value={formData?.values?.subscriptionTierId}
                                onChange={(e) => handleChange('subscriptionTierId', e.target.value)}
                                isInvalid={formData?.errors?.subscriptionTierId}
                            >
                                <option value="">--</option>
                                {availableSubscriptionTiers.map(st => (<option key={st.id} value={st.id}>{st.name}</option>))}
                            </Form.Select>
                            {formData?.errors?.subscriptionTierId && (<span className="error-text">
                                {formData?.errors?.subscriptionTierId}
                            </span>)}
                        </Col>
                    </Form.Group>
                    {(!!formData?.values?.subscriptionTierId) && <Form.Group as={Row} className="mb-3" controlId="frmSubscriptionType">
                        <Form.Label column sm={3}>Subscription Type*</Form.Label>
                        <Col sm={9}>
                            <Form.Select
                                value={formData?.values?.subscriptionTypeId}
                                onChange={(e) => handleChange('subscriptionTypeId', e.target.value)}
                                isInvalid={formData?.errors?.subscriptionTypeId}
                            >
                                <option value="">--</option>
                                {availableSubscriptionTypes.map(st => (<option key={st.id} value={st.id}>{st.name}</option>))}
                            </Form.Select>
                            {formData?.errors?.subscriptionTypeId && (<span className="error-text">
                                {formData?.errors?.subscriptionTypeId}
                            </span>)}
                        </Col>
                    </Form.Group>}
                    {transactionValue>0&&(<Form.Group as={Row} className="mb-3" controlId="frmAddSubscriptionType">
                        <Form.Label column sm={3}>Payment Method*</Form.Label>
                        <Col sm={9}>
                            <Form.Select
                                value={addSubscriptionType}
                                onChange={(e) => handleChange('addSubscriptionType', e.target.value)}
                                isInvalid={formData?.errors?.addSubscriptionType}
                            >
                                <option value="">--</option>
                                {ADD_SUBSCRIPTION_TYPES.map(st => {
                                    const isExistingOpt = st.value === "EXISTING_CARD";
                                    const isDisabled = isExistingOpt && noExistingCards;
                                    return (<option
                                        key={st.value}
                                        value={st.value}
                                        disabled={isDisabled}
                                    >
                                        {`${st.label}${isDisabled ? ' (No cards found)': ''}`}
                                    </option>)
                                })}
                            </Form.Select>
                            {formData?.errors?.addSubscriptionType && (<span className="error-text">
                                {formData?.errors?.addSubscriptionType}
                            </span>)}
                        </Col>
                    </Form.Group>)}
                    {(addSubscriptionType === "EXISTING_CARD")&& (
                         <Form.Group as={Row} className="mb-3" controlId="frmCardId">
                         <Form.Label column sm={3}>Credit Card*</Form.Label>
                         <Col sm={9}>
                             <Form.Select
                                 value={formData?.values?.cardId}
                                 onChange={(e) => handleChange('cardId', e.target.value)}
                                 isInvalid={formData?.errors?.cardId}
                             >
                                 <option value="">--</option>
                                 {cards.map(card => (<option key={card.id} value={card.id}>{diplayCreditCard(card)}</option>))}
                             </Form.Select>
                             {formData?.errors?.cardId && (<span className="error-text">
                                 {formData?.errors?.cardId}
                             </span>)}
                         </Col>
                     </Form.Group>
                    )}
                     {(addSubscriptionType === "NEW_CARD")&& (
                         <Form.Group as={Row} className="mb-3" controlId="frmNewCardToken">
                            {(!stripeKey) && (
                                <Alert variant="warning">{STRIPR_WARNING_MSG}</Alert>
                            )}
                         <Form.Label column sm={3}>New Credit Card*</Form.Label>
                         <Col sm={9}>
                            <div className="border-box">
                                <CardInput/>
                            </div>
                            {stripeError && (<span className="error-text">
                                {stripeError}
                            </span>)}
                         </Col>
                     </Form.Group>
                    )}
                    {(addSubscriptionType === "MANUAL") && (<>
                        <Form.Group as={Row} className="mb-3" controlId="frmTransactionId">
                            <Form.Label column sm={3}>
                                Transaction ID*
                            </Form.Label>
                            <Col sm={9}>
                                <Form.Control
                                    type="text" placeholder=""
                                    value={formData?.values?.transactionId || ''}
                                    onChange={(e) => handleChange('transactionId', e.target.value)}
                                    isInvalid={formData?.errors?.transactionId}
                                />
                                {formData?.errors?.transactionId && (<span className="error-text">
                                    {formData?.errors?.transactionId}
                                </span>)}
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="frmTransactionMode">
                            <Form.Label column sm={3}>Transaction Mode*</Form.Label>
                            <Col sm={9}>
                                <Form.Select
                                    value={formData?.values?.transactionMode}
                                    onChange={(e) => handleChange('transactionMode', e.target.value)}
                                    isInvalid={formData?.errors?.transactionMode}
                                >
                                    <option value="">--</option>
                                    {TRANSACTION_MODES.map(st => (<option key={st.value} value={st.value}>{st.label}</option>))}
                                </Form.Select>
                                {formData?.errors?.transactionMode && (<span className="error-text">
                                    {formData?.errors?.transactionMode}
                                </span>)}
                            </Col>
                        </Form.Group>
                    </>)}
                    <Form.Group as={Row} className="mb-3" controlId="frmStartDate">
                        <Form.Label column sm={3}>Start Date*</Form.Label>
                        <Col sm={9}>
                            <DatePicker
                                dateFormat="dd/MM/yyyy"
                                selected={formData?.values?.startDate}
                                onChange={(date) => handleChange('startDate', date)}
                            />
                             {formData?.errors?.startDate && (<span className="error-text">
                                {formData?.errors?.startDate}
                            </span>)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3" controlId="frmNotes">
                        <Form.Label column sm={3}>
                            Notes
                        </Form.Label>
                        <Col sm={9}>
                            <Form.Control
                                type="text" as={'textarea'} rows={2} placeholder=""
                                value={formData?.values?.notes || ''}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                isInvalid={formData?.errors?.notes}
                            />
                            {formData?.errors?.notes && (<span className="error-text">
                                {formData?.errors?.notes}
                            </span>)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3" controlId="frmAssignLoyaltyPoints">
                        <Col>
                            <Form.Check
                                type="checkbox" label="Assign loyalty points"
                                value={!!formData?.values?.assignLoyaltyPoints}
                                onChange={(e) => handleChange('assignLoyaltyPoints', !formData?.values?.assignLoyaltyPoints)}
                            />
                        </Col>
                    </Form.Group>
                    {selectedSubscriptionType && <Form.Group as={Row} className="mb-3">
                        <h4>Total : {transactionValue} {selectedSubscriptionType?.currencyCode}</h4>
                    </Form.Group>}
                </Form>
                {apiError && (
                    <Alert variant="danger">{apiError}</Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" onClick={onSubmit} disabled={submitting}>
                    {submitting && <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />}
                    Save Subscription
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AddSubscription;